import { useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { PackageRevenueGrouping, PackageRevenueReport as PackageRevenueData, PackageRevenueRow } from '@his/shared';
import { tr } from '@/lib/i18n-core';
import { get, qs } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { money, toIsoDate, todayIso, type CsvColumn } from '@/lib/utils';
import { DataTable, FilterBar, FilterField, StatTile } from '@/components/ui/layout';
import { Alert, Badge, Button, DatePicker, TableSkeleton } from '@/components/ui/primitives';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { CsvExportButton } from '../pharmacy/CsvExportButton';
import type { QueryParams } from '../pharmacy/paging-export';
import { packageKindLabel, packageKindVariant } from './PackagesTab';

const PAGE_SIZE = 50;
const DEFAULT_PERIOD_DAYS = 30;
const MS_PER_DAY = 86_400_000;

interface Filters {
  from: string;
  to: string;
  groupBy: PackageRevenueGrouping;
}

function plainAmount(minor: number): string {
  return (minor / 100).toFixed(2);
}

export function packageRevenueCsvColumns(groupBy: PackageRevenueGrouping): CsvColumn<PackageRevenueRow>[] {
  return [
    ...(groupBy === 'month' ? [{ header: tr('billing', 'month'), value: (row: PackageRevenueRow) => row.period ?? '' }] : []),
    { header: tr('billing', 'code'), value: (row) => row.code },
    { header: tr('billing', 'package'), value: (row) => row.name },
    { header: tr('billing', 'package_kind'), value: (row) => packageKindLabel(row.kind) },
    { header: tr('billing', 'packages_sold'), value: (row) => row.sold },
    { header: tr('billing', 'cancelled'), value: (row) => row.cancelled },
    { header: tr('billing', 'sale_value'), value: (row) => plainAmount(row.saleMinor) },
    { header: tr('billing', 'value_used'), value: (row) => plainAmount(row.consumedMinor) },
    { header: tr('billing', 'medicines_allowance_used'), value: (row) => plainAmount(row.allowanceUsedMinor) },
    { header: tr('billing', 'value_not_used'), value: (row) => plainAmount(row.unusedMinor) },
    { header: tr('billing', 'overruns_billed'), value: (row) => plainAmount(row.overrunMinor) },
    { header: tr('billing', 'credited_on_cancellation'), value: (row) => plainAmount(row.creditedMinor) },
  ];
}

export function PackageRevenueReport() {
  const [draft, setDraft] = useState<Filters>(() => ({
    from: toIsoDate(new Date(Date.now() - (DEFAULT_PERIOD_DAYS - 1) * MS_PER_DAY)),
    to: todayIso(),
    groupBy: 'package',
  }));
  const [applied, setApplied] = useState<Filters | null>(null);
  const [offset, setOffset] = useState(0);
  const params: QueryParams = { from: applied?.from, to: applied?.to, groupBy: applied?.groupBy };

  const report = useQuery({
    queryKey: ['package-revenue', applied, offset],
    queryFn: () => get<PackageRevenueData>(`/package-revenue${qs({ ...params, limit: PAGE_SIZE, offset })}`),
    enabled: applied !== null,
    placeholderData: (previous) => previous,
  });

  const invalid = !draft.from || !draft.to || draft.from > draft.to;
  const run = (event: FormEvent) => {
    event.preventDefault();
    if (invalid) return;
    setApplied({ ...draft });
    setOffset(0);
  };
  const data = report.data;

  return (
    <div className="space-y-4">
      <form onSubmit={run}>
        <FilterBar
          className="mb-0"
          trailing={
            data && applied ? (
              <CsvExportButton<PackageRevenueData, PackageRevenueRow>
                path="/package-revenue"
                params={params}
                pickRows={(response) => response.rows}
                columns={packageRevenueCsvColumns(data.groupBy)}
                filename={`package-revenue-${data.groupBy}-${data.from}-to-${data.to}`}
                disabled={data.rows.length === 0}
              />
            ) : null
          }
        >
          <FilterField label={tr('billing', 'from')} className="w-40">
            <DatePicker value={draft.from} onChange={(from) => setDraft({ ...draft, from })} />
          </FilterField>
          <FilterField label={tr('billing', 'until')} className="w-40">
            <DatePicker value={draft.to} onChange={(to) => setDraft({ ...draft, to })} />
          </FilterField>
          <FilterField label={tr('billing', 'show')}>
            <ChoiceButtons<PackageRevenueGrouping>
              value={draft.groupBy}
              onChange={(groupBy) => setDraft({ ...draft, groupBy })}
              options={[
                { value: 'package', label: tr('billing', 'per_package') },
                { value: 'month', label: tr('billing', 'per_package_and_month') },
              ]}
            />
          </FilterField>
          <Button type="submit" loading={report.isFetching} disabled={invalid}>
            {tr('billing', 'run_report')}
          </Button>
        </FilterBar>
      </form>

      {applied === null ? (
        <div className="rounded-lg border border-dashed p-10 text-center text-sm text-muted-foreground">{tr('billing', 'choose_the_period_and_run_the_report')}</div>
      ) : report.isLoading ? (
        <TableSkeleton />
      ) : report.error ? (
        <Alert variant="destructive">{describeError(report.error)}</Alert>
      ) : data ? (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <StatTile label={tr('billing', 'packages_sold')} value={data.totals.sold} sub={tr('billing', 'x_cancelled', { count: data.totals.cancelled })} />
            <StatTile label={tr('billing', 'sale_value')} value={money(data.totals.saleMinor)} sub={tr('billing', 'before_tax')} />
            <StatTile
              label={tr('billing', 'value_used')}
              value={money(data.totals.consumedMinor)}
              sub={tr('billing', 'of_which_medicines_x', { amount: money(data.totals.allowanceUsedMinor) })}
            />
            <StatTile label={tr('billing', 'value_not_used')} value={money(data.totals.unusedMinor)} />
            <StatTile label={tr('billing', 'overruns_billed')} value={money(data.totals.overrunMinor)} />
            <StatTile label={tr('billing', 'credited_on_cancellation')} value={money(data.totals.creditedMinor)} />
          </div>
          <DataTable
            rows={data.rows}
            refreshing={report.isFetching}
            rowKey={(row) => `${row.packageId}-${row.period ?? ''}`}
            empty={tr('billing', 'no_packages_sold_in_this_period')}
            columns={[
              ...(data.groupBy === 'month' ? [{ header: tr('billing', 'month'), cell: (row: PackageRevenueRow) => <span className="tabular">{row.period}</span> }] : []),
              {
                header: tr('billing', 'package'),
                cell: (row) => (
                  <span className="flex flex-wrap items-center gap-2">
                    {row.name}
                    <Badge variant={packageKindVariant(row.kind)}>{packageKindLabel(row.kind)}</Badge>
                  </span>
                ),
              },
              { header: tr('billing', 'packages_sold'), align: 'right', cell: (row) => <span className="tabular">{row.sold}</span> },
              { header: tr('billing', 'cancelled'), align: 'right', cell: (row) => <span className="tabular">{row.cancelled}</span> },
              { header: tr('billing', 'sale_value'), align: 'right', cell: (row) => <span className="tabular">{money(row.saleMinor)}</span> },
              { header: tr('billing', 'value_used'), align: 'right', cell: (row) => <span className="tabular">{money(row.consumedMinor)}</span> },
              { header: tr('billing', 'medicines_allowance_used'), align: 'right', hideBelow: 'lg', cell: (row) => <span className="tabular">{money(row.allowanceUsedMinor)}</span> },
              { header: tr('billing', 'value_not_used'), align: 'right', cell: (row) => <span className="tabular">{money(row.unusedMinor)}</span> },
              { header: tr('billing', 'overruns_billed'), align: 'right', cell: (row) => <span className="tabular">{money(row.overrunMinor)}</span> },
              { header: tr('billing', 'credited_on_cancellation'), align: 'right', hideBelow: 'md', cell: (row) => <span className="tabular">{money(row.creditedMinor)}</span> },
            ]}
          />
          {data.totalRows > PAGE_SIZE && (
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>{tr('billing', 'rows_x_to_y_of_z', { first: offset + 1, last: offset + data.rows.length, total: data.totalRows })}</span>
              <div className="flex gap-2">
                <Button type="button" variant="outline" size="sm" disabled={offset === 0 || report.isFetching} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>
                  {tr('billing', 'previous_page')}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={offset + PAGE_SIZE >= data.totalRows || report.isFetching}
                  onClick={() => setOffset(offset + PAGE_SIZE)}
                >
                  {tr('billing', 'next_page')}
                </Button>
              </div>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}
